import { useMatcher } from "utils/MatcherProvider";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { CircularProgress, Divider, Stack, styled } from "@mui/material";
import { useDuplicates } from "../services/useDuplicates";
import { useContext, useEffect, useState, useMemo, useCallback } from "react";
import { context } from "utils/Provider";
import colors from "assets/theme-dark/base/colors";
import KeywordSearch from "./KeywordSearch";
import CustomTable from "../../matcher/components/CustomTable";
import { enqueueSnackbar } from "notistack";
import { DuplicateSelector } from "./DuplicateSelector";

interface TableRow {
  FX_UNIQUE_ID: string;
  [key: string]: unknown;
}

interface TableColumn {
  Header: string;
  accessor: string;
}

interface TableData {
  columns: TableColumn[];
  rows: TableRow[];
}

const Row = styled(Stack)`
  flex-direction: row;
  gap: 1rem;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const CardWrapper = styled("div")`
  width: 100%;
  background: ${colors.background.default};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  padding: 0.5rem;
  border: 1px solid ${colors.grey[100]};
`;

export function Duplicates() {
  const { phaseData, changeView } = useMatcher();
  const { selectedCube } = useContext(context);

  const {
    onSelectDuplicate,
    selectedDuplicate,
    loading,
    error,
    matcherTableData,
    changePage,
    changePageSize,
    pageData,
    mergeData,
    handleMergeRecords,
    onSelectMatch,
  } = useDuplicates({ matcherTableName: phaseData.matcherTable });

  const [hasSearched, setHasSearched] = useState(false);
  const [searchTableData, setSearchTableData] = useState<TableRow[]>([]);
  const [searchTotalCount, setSearchTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedColumn, setSelectedColumn] = useState("");

  const tableData: TableData = useMemo(() => {
    if (!matcherTableData || !Array.isArray(matcherTableData) || matcherTableData.length === 0) {
      return { columns: [], rows: [] };
    }

    const columns = Object.keys(matcherTableData[0].value)?.map((key) => ({
      Header: key,
      accessor: key,
    }));

    const rows = matcherTableData?.map((item) => ({
      ...item.value,
    })) as TableRow[];

    return { columns, rows };
  }, [matcherTableData]);

  useEffect(() => {
    if (!hasSearched && matcherTableData && Array.isArray(matcherTableData)) {
      setSearchTableData(tableData.rows);
      setSearchTotalCount(pageData.count);
    }
  }, [tableData.rows, matcherTableData, hasSearched, pageData.count]);

  const resetSearch = useCallback(() => {
    setSearchTerm("");
    setSelectedColumn("");
    setHasSearched(false);
    setSearchTableData(tableData.rows);
    setSearchTotalCount(pageData.count);
    changePage(0);
  }, [tableData.rows, pageData.count, changePage]);

  const handleRowSelect = (id: string) => {
    const source = hasSearched ? searchTableData : tableData.rows;

    const row = source.find((row: TableRow, index: number) =>
      row.FX_UNIQUE_ID ? row.FX_UNIQUE_ID === id : index.toString() === id
    );

    if (!row) {
      enqueueSnackbar(`Row with ${phaseData.identifier} = ${id} not found`, { variant: "error" });
      return;
    }
    const identifierValue = row[phaseData.identifier] as string;
    if (!identifierValue) {
      enqueueSnackbar(`Identifier ${phaseData.identifier} not found in row`, { variant: "error" });
      return;
    }
    onSelectMatch({
      cubeId: selectedCube.cubeId,
      value: identifierValue,
      rowId: id,
    });
  };

  const content = (() => {
    if (error) {
      return <MDTypography sx={{ color: colors.error.main }}>{error || "An error occurred"}</MDTypography>;
    }
    if (loading.service) {
      return (
        <CircularProgress
          sx={{
            position: "absolute",
            marginLeft: "auto",
            marginRight: "auto",
            left: 0,
            right: 0,
            marginTop: "auto",
            marginBottom: "auto",
            top: 0,
            bottom: 0,
          }}
        />
      );
    }
    return (
      <Stack spacing={1}>
        <CardWrapper sx={{ maxHeight: "fit-content" }}>
          {/* <Stack spacing={2}> */}
          <DuplicateSelector
            duplicates={phaseData?.duplicates || []}
            selectedDuplicate={selectedDuplicate}
            onSelectDuplicate={onSelectDuplicate}
            mergeData={mergeData}
            handleMergeRecords={handleMergeRecords}
            loading={loading}
            phaseData={phaseData}
            selectedCube={selectedCube}
            tableData={tableData}
            hasSearched={hasSearched}
            searchTableData={searchTableData}
            changeView={changeView}
          />
          <Row justifyContent="space-between" sx={{ alignItems: "center" }}>
            <Row sx={{ gap: "0.1rem" }} />
            <KeywordSearch
              tableName={phaseData?.matcherTable}
              tableData={tableData}
              pageSize={pageData?.pageSize}
              currentPage={pageData.page || 0}
              setCurrentPage={changePage}
              setTableData={setSearchTableData}
              setTotalCount={setSearchTotalCount}
              setHasSearched={setHasSearched}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              selectedColumn={selectedColumn}
              setSelectedColumn={setSelectedColumn}
              resetSearch={resetSearch}
            />
          </Row>
          {/* </Stack> */}
        </CardWrapper>
        <Divider sx={{ margin: "1rem 0" }} />
        <CardWrapper>
          <CustomTable
            total={hasSearched ? searchTotalCount : pageData.count}
            loading={loading.service}
            table={hasSearched ? { columns: tableData.columns, rows: searchTableData } : tableData}
            pageSize={pageData.pageSize}
            currentPage={pageData.page || 0}
            setPage={changePage}
            setPageSizeData={changePageSize}
            selectedRowId={mergeData?.rowId}
            onRowSelect={handleRowSelect}
            identifierColumn={phaseData?.identifier}
          />
        </CardWrapper>
      </Stack>
    );
  })();

  return (
    <MDBox
      sx={{
        borderRadius: "0",
        boxShadow: "none",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        padding: "1rem",
      }}
    >
      <Stack sx={{ flex: 1, position: "relative" }}>{content}</Stack>
    </MDBox>
  );
}
